import React, { useState, useEffect } from 'react';
import { useAuthStore } from '@/app/store/authStore';
import { useFeedStore } from '@/app/store/feedStore';
import { useProcessFeeds } from '@/shared/hooks/useProcessFeeds';
import { useTheme, type ThemeMode } from '@/shared/stores/themeStore';
import {
  UserIcon,
  BellIcon,
  RefreshCwIcon,
  SaveIcon,
  PaletteIcon,
  SunIcon,
  MoonIcon,
  MonitorIcon,
  LayersIcon,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { cn } from '@/shared/utils/cn';
import { Spinner } from '@/shared/components/Spinner';
import { EmptyState } from '@/shared/components/EmptyState';

const userSettingsSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  atlassianAccountId: z.string().trim().max(128, 'Account ID is too long').optional(),
  emailNotifications: z.boolean(),
  notificationFrequency: z.enum(['Immediate', 'Daily', 'Weekly']),
});

type UserSettingsData = z.infer<typeof userSettingsSchema>;

type TabId = 'profile' | 'notifications' | 'appearance' | 'feeds';

const THEME_OPTIONS: { value: ThemeMode; label: string; icon: React.ElementType }[] = [
  { value: 'light', label: 'Light', icon: SunIcon },
  { value: 'dark', label: 'Dark', icon: MoonIcon },
  { value: 'system', label: 'System', icon: MonitorIcon },
];

export const Settings: React.FC = () => {
  const {
    user,
    updateProfile,
    isLoading: authLoading,
    error: authError,
    notificationPreferences,
    loadNotificationPreferences,
    updateNotificationPreferences,
  } = useAuthStore();
  const { productTypes, loadProductTypes } = useFeedStore();
  const { processFeeds, isProcessing } = useProcessFeeds();
  const { mode: themeMode, setMode: setThemeMode } = useTheme();
  const [activeTab, setActiveTab] = useState<TabId>('profile');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<UserSettingsData>({
    resolver: zodResolver(userSettingsSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: user?.email || '',
      atlassianAccountId: '',
      emailNotifications: true,
      notificationFrequency: 'Daily',
    },
  });

  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email,
        atlassianAccountId: user.atlassianAccountId || '',
        emailNotifications: notificationPreferences?.emailNotifications ?? true,
        notificationFrequency: notificationPreferences?.notificationFrequency ?? 'Daily',
      });
    }
  }, [user, reset, notificationPreferences]);

  // Load the user's saved notification preferences when they open the Notifications tab.
  useEffect(() => {
    if (activeTab === 'notifications') {
      loadNotificationPreferences();
    }
  }, [activeTab, loadNotificationPreferences]);

  useEffect(() => {
    if (activeTab === 'feeds' && productTypes.length === 0) {
      loadProductTypes();
    }
  }, [activeTab, productTypes.length, loadProductTypes]);

  // Seed the notification form fields once the saved preferences arrive.
  useEffect(() => {
    if (notificationPreferences) {
      setValue('emailNotifications', notificationPreferences.emailNotifications);
      setValue('notificationFrequency', notificationPreferences.notificationFrequency);
    }
  }, [notificationPreferences, setValue]);

  // Show auth errors if they exist
  useEffect(() => {
    if (authError) {
      toast.error(authError);
    }
  }, [authError]);

  const onSubmit = async (data: UserSettingsData) => {
    try {
      const { success, emailConfirmationRequired } = await updateProfile(data.firstName, data.lastName, data.email, data.atlassianAccountId);

      if (success) {
        if (emailConfirmationRequired) {
          // Name changes are saved immediately; the email change is pending until the user
          // confirms via the link sent to the new address. Acknowledge both, and give it extra
          // on-screen time so the email note isn't missed.
          toast.success(
            `Profile updated. We've also sent a confirmation link to ${data.email} — your email address will change once you confirm it from that inbox.`,
            { duration: 8000 }
          );
        } else {
          toast.success('Profile updated successfully');
        }
      }
      // Error handling is done in the store and will show via useEffect above
    } catch {
      toast.error('Failed to update profile');
    }
  };

  const onSubmitNotifications = async (data: UserSettingsData) => {
    try {
      const success = await updateNotificationPreferences(data.emailNotifications, data.notificationFrequency);
      if (success) {
        toast.success('Notification preferences saved');
      }
      // Error handling is done in the store and will show via the auth-error useEffect above
    } catch {
      toast.error('Failed to save notification preferences');
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-muted-foreground">Please log in to access settings.</p>
      </div>
    );
  }

  const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
    { id: 'profile', label: 'Profile', icon: UserIcon },
    { id: 'notifications', label: 'Notifications', icon: BellIcon },
    { id: 'appearance', label: 'Appearance', icon: PaletteIcon },
    { id: 'feeds', label: 'RSS Feeds', icon: RefreshCwIcon },
  ];

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Manage your account preferences and RSS feed settings
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-6 overflow-x-auto">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground'
                )}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'profile' && (
          <div className="card">
            <div className="border-b border-border p-5">
              <h3 className="text-base font-semibold text-foreground">Profile Settings</h3>
              <p className="mt-0.5 text-sm text-muted-foreground">Update your personal information</p>
            </div>
            <div className="p-5">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label htmlFor="firstName" className="label">
                      First Name
                    </label>
                    <input
                      {...register('firstName')}
                      id="firstName"
                      type="text"
                      className="input mt-1.5"
                      placeholder="Enter your first name"
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-danger">{errors.firstName.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="lastName" className="label">
                      Last Name
                    </label>
                    <input
                      {...register('lastName')}
                      id="lastName"
                      type="text"
                      className="input mt-1.5"
                      placeholder="Enter your last name"
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-danger">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="label">
                    Email Address
                  </label>
                  <input
                    {...register('email')}
                    id="email"
                    type="email"
                    className="input mt-1.5"
                    placeholder="Enter your email"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-danger">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="atlassianAccountId" className="label">
                    Jira Account ID
                  </label>
                  <input
                    {...register('atlassianAccountId')}
                    id="atlassianAccountId"
                    type="text"
                    className="input mt-1.5"
                    placeholder="e.g., 557058:f58131cb-b67d-43c7-b30d-6b58d40bd077"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Your Atlassian account ID, used to set you as assignee/reporter when tasks are
                    exported to Jira. Find it in Jira under Profile → Account, or via the URL of your
                    profile page. Leave blank if you don&apos;t use Jira — the export will simply omit you.
                  </p>
                  {errors.atlassianAccountId && (
                    <p className="mt-1 text-sm text-danger">{errors.atlassianAccountId.message}</p>
                  )}
                </div>

                <div className="flex justify-end">
                  <button type="submit" disabled={authLoading} className="btn-primary">
                    {authLoading ? <Spinner className="text-primary-foreground" /> : <SaveIcon className="h-4 w-4" />}
                    <span>{authLoading ? 'Saving…' : 'Save Changes'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="card">
            <div className="border-b border-border p-5">
              <h3 className="text-base font-semibold text-foreground">Notification Preferences</h3>
              <p className="mt-0.5 text-sm text-muted-foreground">Customize how you receive updates</p>
            </div>
            <div className="p-5">
              <form onSubmit={handleSubmit(onSubmitNotifications)} className="space-y-6">
                <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-4">
                  <div>
                    <h4 className="text-sm font-medium text-foreground">Email Notifications</h4>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      Receive email updates about new RSS items
                    </p>
                  </div>
                  <label className="relative inline-flex shrink-0 cursor-pointer items-center">
                    <input {...register('emailNotifications')} type="checkbox" className="peer sr-only" />
                    <div className="peer h-6 w-11 rounded-full bg-muted transition-colors after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow-xs after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-focus-visible:outline-hidden peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-card" />
                  </label>
                </div>

                <div>
                  <label htmlFor="notificationFrequency" className="label">
                    Notification Frequency
                  </label>
                  <select {...register('notificationFrequency')} id="notificationFrequency" className="input mt-1.5">
                    <option value="Immediate">Immediate</option>
                    <option value="Daily">Daily Digest</option>
                    <option value="Weekly">Weekly Summary</option>
                  </select>
                  <p className="mt-1.5 text-sm text-muted-foreground">
                    How often we bundle and send your update emails.
                  </p>
                  {errors.notificationFrequency && (
                    <p className="mt-1 text-sm text-danger">{errors.notificationFrequency.message}</p>
                  )}
                </div>

                <div className="flex justify-end">
                  <button type="submit" disabled={authLoading} className="btn-primary">
                    {authLoading ? <Spinner className="text-primary-foreground" /> : <SaveIcon className="h-4 w-4" />}
                    <span>{authLoading ? 'Saving…' : 'Save Preferences'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'appearance' && (
          <div className="card">
            <div className="border-b border-border p-5">
              <h3 className="text-base font-semibold text-foreground">Appearance</h3>
              <p className="mt-0.5 text-sm text-muted-foreground">Choose how UpdateRadar looks on this device</p>
            </div>
            <div className="p-5">
              <div>
                <span className="label">Theme</span>
                <p className="mb-3 mt-0.5 text-sm text-muted-foreground">
                  System follows your operating system&apos;s light or dark setting.
                </p>
                <div
                  role="group"
                  aria-label="Theme"
                  className="inline-grid grid-cols-3 gap-1 rounded-lg border border-border bg-muted p-1"
                >
                  {THEME_OPTIONS.map((option) => {
                    const isActive = themeMode === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setThemeMode(option.value)}
                        aria-pressed={isActive}
                        className={cn(
                          'flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-card text-primary shadow-xs ring-1 ring-primary/30'
                            : 'text-muted-foreground hover:text-foreground'
                        )}
                      >
                        <option.icon className="h-4 w-4" />
                        <span>{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'feeds' && (
          <div className="space-y-6">
            {user?.isAdmin && (
              <div className="card">
                <div className="border-b border-border p-5">
                  <h3 className="text-base font-semibold text-foreground">RSS Feed Management</h3>
                  <p className="mt-0.5 text-sm text-muted-foreground">Process and manage your RSS feeds</p>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-foreground">Process All Feeds</h4>
                      <p className="mt-0.5 text-sm text-muted-foreground">
                        Manually trigger processing of all RSS feeds
                      </p>
                    </div>
                    <button onClick={processFeeds} disabled={isProcessing} className="btn-primary shrink-0">
                      <RefreshCwIcon className={cn('h-4 w-4', isProcessing && 'animate-spin')} />
                      <span>{isProcessing ? 'Processing…' : 'Process Feeds'}</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Product Types */}
            <div className="card">
              <div className="border-b border-border p-5">
                <h3 className="text-base font-semibold text-foreground">Product Types</h3>
                <p className="mt-0.5 text-sm text-muted-foreground">Available product categories for RSS feeds</p>
              </div>
              <div className="p-5">
                {productTypes.length > 0 ? (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {productTypes.map((productType) => (
                      <div key={productType.value} className="rounded-lg border border-border p-4">
                        <h5 className="font-medium text-foreground">{productType.name}</h5>
                        <p className="mt-1 text-sm text-muted-foreground">{productType.description}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon={LayersIcon}
                    title="No product types found"
                    description="Product types will appear here once loaded"
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
